import $ from 'jquery';

/** Tables that call `$.fn.DataTable` from PHP inline scripts (server-side / custom config). */
/** `#table-users` is initialized in `partials/control_panel/users.js` to avoid double-loading. */
const SERVER_SIDE_TABLE_SELECTOR =
    '#table-returns, #table-stock-notifications, #table-lost-sales, #table-orders, #table-records';

/**
 * Sticky readiness contract for legacy inline DataTables init scripts.
 *
 * Many admin views ship inline `<script>` blocks that look like:
 *
 *     $(document).one('datatables:ready', function () {
 *         $('#table-x').DataTable({...});
 *     });
 *
 * That pattern is FRAGILE because `initDataTables()` loads the DataTables
 * chunk via a dynamic `import()`. The order of (a) the inline handler
 * registering on the document and (b) the chunk resolving + firing
 * `datatables:ready` is not guaranteed across browsers / HTTP-2 preload /
 * cached chunks. On dev2 we hit a race where the inline handler ran via
 * jQuery's $(document).ready Deferred BEFORE the import resolved, so
 * `$.fn.DataTable` was still undefined.
 *
 * The fix:
 *   1. Expose `window.dataTablesReady` (Promise) — the canonical, race-free
 *      hook for new code: `window.dataTablesReady.then(() => $('#x').DataTable(...))`.
 *   2. Keep firing `datatables:ready` on document AND replay it whenever a
 *      new handler is registered after load (via `onDataTablesReady`).
 *   3. Expose `window.onDataTablesReady(cb)` — a drop-in for the legacy
 *      `$(document).one('datatables:ready', cb)` calls that runs `cb`
 *      synchronously if DataTables is already loaded, otherwise queues it.
 */

let dataTablesReadyResolve;
const dataTablesReadyPromise = new Promise(function (resolve) {
    dataTablesReadyResolve = resolve;
});
let dataTablesLoaded = false;

if (typeof window !== 'undefined') {
    window.dataTablesReady = dataTablesReadyPromise;
    window.onDataTablesReady = function (cb) {
        if (typeof cb !== 'function') return;
        if (dataTablesLoaded) {
            try { cb(); } catch (err) { console.error('[DataTables] handler error', err); }
            return;
        }
        dataTablesReadyPromise.then(cb).catch(function (err) {
            console.error('[DataTables] handler error', err);
        });
    };
}

export function initDataTables() {
    const needsPlugin =
        $('.dt-table').length ||
        $('.dt-exportable').length ||
        $(SERVER_SIDE_TABLE_SELECTOR).length;

    if (!needsPlugin) return;

    import('./vendor/datatables.js')
        .then(function () {
            if ($('.dt-table').length) {
                $('.dt-table').DataTable({
                    responsive: true,
                });

                $('#DataTables_Table_0_wrapper')
                    .find('select')
                    .addClass('form-control');
                $('#DataTables_Table_0_filter')
                    .find('input[type="search"]')
                    .addClass('form-control');
            }

            if ($('.dt-exportable').length) {
                $('.dt-exportable').DataTable({
                    dom: 'B<"clear">lfrtip',
                    buttons: [
                        {
                            extend: 'excel',
                            exportOptions: { columns: 'thead th:not(.no-export)' },
                        },
                        {
                            extend: 'csv',
                            exportOptions: { columns: 'thead th:not(.no-export)' },
                        },
                        {
                            extend: 'pdf',
                            exportOptions: { columns: 'thead th:not(.no-export)' },
                        },
                    ],
                    responsive: true,
                });

                $('#DataTables_Table_0_wrapper')
                    .find('select')
                    .addClass('form-control');
                $('#DataTables_Table_0_filter')
                    .find('input[type="search"]')
                    .addClass('form-control');
            }

            // Mark loaded + resolve the sticky promise BEFORE firing the legacy
            // event, so handlers using `window.onDataTablesReady()` registered
            // late still see `dataTablesLoaded === true`.
            dataTablesLoaded = true;
            if (typeof dataTablesReadyResolve === 'function') {
                dataTablesReadyResolve();
            }

            // Legacy contract: inline scripts in views may `.one('datatables:ready', ...)`.
            $(document).trigger('datatables:ready');
        })
        .catch(function (err) {
            // Without this, a failed chunk would silently leave admin tables empty.
            console.error('[DataTables] failed to load chunk', err);
        });
}
